import { Radio, RadioGroup } from "@headlessui/react";
import { type Dtos } from "@inrev/common";
import { cn } from "../../../../lib/utils";
import { Text } from "../../../../primitives";
import { PRO_CON_IMPORTANCE_LABELS, PRO_CON_IMPORTANCES } from "../../decisioningLabels";
import { ImportanceMeter } from "./ImportanceMeter";

type Importance = Dtos.Decisioning.ProConImportance;

export const IMPORTANCE_DESCRIPTIONS: Record<Importance, string> = {
	1: "Background that rounds out the picture.",
	2: "Weighs on the decision, but heavier factors can outweigh it.",
	3: "Carries heavy weight. Few factors outweigh it.",
	4: "Cannot be outweighed. Needs a rationale.",
};

export type ImportancePickerProps = {
	value: Importance;
	onChange: (importance: Importance) => void;
	side: Dtos.Decisioning.ProConKind;
};

/** Four equal buttons, each with a rising meter that fills in the side color when selected. */
export const ImportancePicker = ({ value, onChange, side }: ImportancePickerProps) => (
	<div className="flex flex-col gap-1.5">
		<RadioGroup
			value={value}
			onChange={onChange}
			aria-label="Importance"
			data-testid="importance-picker"
			className="grid grid-cols-4 gap-1.5"
		>
			{PRO_CON_IMPORTANCES.map((level) => (
				<Radio
					key={level}
					value={level}
					data-testid={`importance-option-${level}`}
					className={cn(
						"flex min-w-0 cursor-pointer select-none flex-col items-center gap-1 rounded-md border border-bc-default bg-surface-base px-1 py-1.5 outline-none transition-colors hover:bg-surface-light focus-visible:ring-2 focus-visible:ring-bc-focus",
						"data-[checked]:border-bc-focus data-[checked]:bg-surface-accent",
					)}
				>
					<ImportanceMeter variant="rising" weight={level} side={side} muted={value !== level} />
					<Text type="caption" color="primary" className="max-w-full leading-4">
						{PRO_CON_IMPORTANCE_LABELS[level]}
					</Text>
				</Radio>
			))}
		</RadioGroup>
		<Text
			type="caption"
			color="tertiary"
			className="font-normal"
			data-testid="importance-description"
		>
			{IMPORTANCE_DESCRIPTIONS[value]}
		</Text>
	</div>
);
